import useAnalytics from "hooks/analytics";
import useBookingFlowRedesign from "hooks/useBookingFlowRedesign";
import React, { useState } from "react";
import { UserActionEvent } from "utils/analytics/events";
import { ReactComponent as Clock } from "../../assets/images/timer.svg";
import { ReactComponent as Zap } from "../../assets/images/zap.svg";
import {
    Button,
    Footer,
    Header,
    OptionDetail,
    RadioCardExpanded,
} from "../../components";
import useLanguage from "../../hooks/language";
import "./labSelectionPage.css";
import LabSelectionPageV2 from "./lab-selection-v2";
import RamqPopover, { LabType } from "./ramqPopover";

export default function LabSelectionPage({
    handleLabSelectionSubmission,
    labType,
    setLabType,
}: {
    handleLabSelectionSubmission: (labType: LabType) => void;
    labType: LabType | undefined;
    setLabType: (arg: LabType) => void;
}): React.ReactElement {
    const isBookingFlowRedesign = useBookingFlowRedesign();
    const { translations } = useLanguage();
    const [analytics] = useAnalytics();
    const [isRAMQPopoverOpen, setIsRAMQPopoverOpen] = useState<boolean>(false);

    if (isBookingFlowRedesign) {
        return (
            <LabSelectionPageV2
                handleLabSelectionSubmission={handleLabSelectionSubmission}
                labType={labType}
                setLabType={setLabType}
            />
        );
    }

    const onPopoverCtaClick = () => {
        analytics
            ?.cdp()
            ?.trackEvent(UserActionEvent.LocationPopupPopoverClicked);
    };
    const onPopoverToggle = () =>
        analytics
            ?.cdp()
            ?.trackEvent(UserActionEvent.LocationPopupToggleClicked);
    const onSelectLabType = (labType: LabType) => {
        const eventProperties = {
            selection: labType,
        };
        analytics
            ?.cdp()
            ?.trackEvent(
                UserActionEvent.LocationPopupLabChanged,
                eventProperties
            );
        setLabType(labType);
    };

    return (
        <div className="page">
            <Header />
            <div className="lab-selection-page__container">
                <h1 className="lab-selection-page__title">
                    {translations.labSelectionPage.title}
                </h1>
                <div className="lab-selection-page__turnaround-content">
                    <RadioCardExpanded
                        checked={labType === "public"}
                        name="labType"
                        value="public"
                        onSelect={onSelectLabType}
                        icon={<Clock />}
                    >
                        <div className="lab-selection-page__turnaround-card-content">
                            <span className="lab-selection-page__turnaround-card-title">
                                {
                                    translations.availabilityPublicPage
                                        .publicLabOptionTitle
                                }
                                <sup>1</sup>
                            </span>
                            <div>
                                <OptionDetail
                                    text={
                                        translations.availabilityPublicPage
                                            .publicLabOptionBenefitOne
                                    }
                                />
                                <OptionDetail
                                    text={
                                        translations.availabilityPublicPage
                                            .publicLabOptionBenefitTwo
                                    }
                                />
                            </div>
                            <span className="lab-selection-page__turnaround-card-subtitle">
                                {
                                    translations.availabilityPublicPage
                                        .publicLabOptionPrice
                                }{" "}
                                <span className="lab-selection-page__turnaround-card-note">
                                    {
                                        translations.availabilityPublicPage
                                            .publicLabOptionPriceNote
                                    }
                                </span>
                            </span>
                        </div>
                    </RadioCardExpanded>

                    <RadioCardExpanded
                        checked={labType === "private"}
                        name="labType"
                        value="private"
                        onSelect={onSelectLabType}
                        icon={<Zap />}
                    >
                        <div className="lab-selection-page__turnaround-card-content">
                            <span className="lab-selection-page__turnaround-card-title">
                                {
                                    translations.availabilityPublicPage
                                        .privateLabOptionTitle
                                }{" "}
                            </span>
                            <div>
                                <OptionDetail
                                    text={
                                        translations.availabilityPublicPage
                                            .privateLabOptionBenefitOne
                                    }
                                    sup="2"
                                />
                                <OptionDetail
                                    text={
                                        translations.availabilityPublicPage
                                            .privateLabOptionBenefitTwo
                                    }
                                />
                            </div>
                            <span className="lab-selection-page__turnaround-card-subtitle">
                                {
                                    translations.availabilityPublicPage
                                        .privateLabOptionPriceRange
                                }{" "}
                                <span className="lab-selection-page__turnaround-card-note">
                                    {
                                        translations.availabilityPublicPage
                                            .privateLabOptionPriceNote
                                    }
                                </span>
                            </span>
                        </div>
                    </RadioCardExpanded>
                </div>
                <p className="final-price-note__text">
                    {translations.labSelectionPage.finalPriceNote}
                </p>
                {labType === "public" && (
                    <div className="lab-selection-page__ramq-row">
                        <span className="lab-selection-page__ramq-label">
                            {translations.availabilityPublicPage.ramqValidLabel}
                        </span>
                        <RamqPopover
                            isRAMQPopoverOpen={isRAMQPopoverOpen}
                            setIsRAMQPopoverOpen={setIsRAMQPopoverOpen}
                            setLabType={setLabType}
                            onCtaClick={onPopoverCtaClick}
                            onToggle={onPopoverToggle}
                        />
                    </div>
                )}

                <Button
                    type="primary"
                    disabled={!labType}
                    onClick={() => {
                        if (labType) {
                            handleLabSelectionSubmission(labType);
                        }
                    }}
                    className="lab-selection-page__button"
                >
                    {translations.labSelectionPage.button}{" "}
                </Button>
                <div className="footnote__container">
                    <span className="footnote__text">
                        {translations.labSelectionPage.footnote.publicLabDelay}
                    </span>
                    <span className="footnote__text">
                        {
                            translations.labSelectionPage.footnote
                                .insurancePlansReimbursement
                        }
                    </span>
                </div>
            </div>
            <Footer />
        </div>
    );
}
